import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, Linkedin, Mail, ArrowDownRight } from "lucide-react";
import { LINKS } from "../../../data/content";
import { CodeCard } from "../../portfolio/CodeCard";
import { scrollToSection } from "../files";

const useTypewriter = (text, speed = 55) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    setN(0);
    const id = setInterval(() => {
      setN((v) => {
        if (v >= text.length) {
          clearInterval(id);
          return v;
        }
        return v + 1;
      });
    }, speed);
    return () => clearInterval(id);
  }, [text, speed]);
  return text.slice(0, n);
};

export const HeroSection = ({ t, lang }) => {
  const typedTitle = useTypewriter(t.hero.title);

  return (
    <div className="grid lg:grid-cols-12 gap-12 items-center min-h-[70vh]">
      <div className="lg:col-span-7">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="font-mono text-xs text-[var(--cm)] mb-6"
        >
          {"<!-- "}{t.hero.overline}{" -->"}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-mono"
        >
          <div className="text-sm text-[var(--muted)] mb-2">
            <span className="text-[var(--kw)]">&lt;h1</span>{" "}
            <span className="text-[var(--var)]">class</span>
            <span className="text-[var(--muted)]">=</span>
            <span className="text-[var(--str)]">"developer"</span>
            <span className="text-[var(--kw)]">&gt;</span>
          </div>
          <h1
            data-testid="hero-name"
            className="font-mono text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[var(--text)] pl-4 sm:pl-6"
          >
            İBRAHİM ŞİŞMAN
          </h1>
          <div data-testid="hero-typed-title" className="pl-4 sm:pl-6 mt-3 font-mono text-lg sm:text-2xl text-[#FF5722]">
            {typedTitle}
            <span className="cursor-blink">▍</span>
          </div>
          <div className="text-sm text-[var(--muted)] mt-2">
            <span className="text-[var(--kw)]">&lt;/h1&gt;</span>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-8 max-w-xl font-mono text-[13px] leading-relaxed text-[var(--muted)]"
        >
          <span className="text-[var(--cm)]">/* </span>
          {t.hero.subtitle}
          <span className="text-[var(--cm)]"> */</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="mt-10 flex flex-wrap items-center gap-3"
        >
          <a
            data-testid="hero-cv-download-btn"
            href="/cv-ibrahim-sisman.pdf"
            download="Ibrahim-Sisman-CV.pdf"
            className="rounded-none bg-[#FF5722] text-white hover:bg-[#FF7A50] px-6 py-3.5 font-mono text-xs tracking-wider transition-[background-color,transform] duration-300 hover:-translate-y-0.5 flex items-center gap-2"
          >
            $ npm run cv:download <Download size={13} />
          </a>
          <button
            data-testid="hero-contact-btn"
            onClick={() => scrollToSection("contact")}
            className="rounded-none border border-[var(--border)] text-[var(--text)] hover:border-[#FF5722]/60 px-6 py-3.5 font-mono text-xs tracking-wider transition-[border-color,transform] duration-300 hover:-translate-y-0.5 flex items-center gap-2"
          >
            $ {t.hero.cta.toLowerCase()} <ArrowDownRight size={13} />
          </button>
          <div className="flex items-center gap-2">
            <a
              data-testid="hero-linkedin-link"
              href={LINKS.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 border border-[var(--border)] text-[var(--muted)] hover:text-[#FF5722] hover:border-[#FF5722]/50 transition-colors"
            >
              <Linkedin size={15} />
            </a>
            <a
              data-testid="hero-email-link"
              href={`mailto:${LINKS.email}`}
              className="p-3 border border-[var(--border)] text-[var(--muted)] hover:text-[#FF5722] hover:border-[#FF5722]/50 transition-colors"
            >
              <Mail size={15} />
            </a>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7, delay: 0.5 }}
        className="hidden lg:flex lg:col-span-5 justify-end"
      >
        <CodeCard lang={lang} />
      </motion.div>
    </div>
  );
};
